import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { origemAtual } from "@/lib/navegacao";
import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, UserX, UserCheck, AlertTriangle, Users } from "lucide-react";
import { AdminLista, AdminListaItem, AdminTabela } from "@/components/admin/admin-lista";
import { PageHeader, ALVO_TOQUE, ALVO_TOQUE_BOTAO } from "@/components/layout/page-header";
import { PageToolbar, PageSearch } from "@/components/layout/page-toolbar";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow,
  DataTableHead, DataTableCell, DataTableNum, DataTableFooter,
} from "@/components/data/data-table";
import { StatusBadge } from "@/components/data/status-badge";
import { EmptyState, NadaEncontrado } from "@/components/data/empty-state";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { combina } from "@/lib/busca";
import {
  useFuncionarios, useCreateFuncionario, useUpdateFuncionario, useDeleteFuncionario,
  useVinculosAtivos, useVinculosDoFuncionario, useQuinzenaAtiva, useDiariasByQuinzena,
} from "@/hooks/use-equipe";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { perfilDoFuncionario, PERFIL_META, type PerfilFuncionario } from "@/lib/equipe-perfil";
import { SetoresDoEncarregado, type VinculoForm } from "@/components/equipe/SetoresDoEncarregado";
import { useSetores } from "@/hooks/use-setores";
import type { Funcionario } from "@shared/schema";

const emptyForm = {
  nome: "",
  apelido: "",
  cargo: "Diarista",
  valorDiaria: "80.00",
  pixChave: "",
  pixTipo: "",
  salarioMensal: "",
  formaPagamento: "MENSAL",
  adiantamentoPct: "50",
  variavelMaxMensal: "0",
  valorKgColheita: "",
};

const PERFIS: PerfilFuncionario[] = ["DIARISTA", "MENSALISTA", "ENCARREGADO"];

const COLUNAS_FUNCIONARIOS = [null, "9rem", "8rem", "12rem", "10rem", "12rem", "6rem"];

// ⚠️ Constante de MÓDULO (dep de useMemo dentro do hook).
const PADRAO_FUNCIONARIOS = { q: "", inativos: false };

export default function EquipeFuncionarios() {
  // Busca NO CLIENTE, sobre a lista já carregada — `inativos` continua sendo
  // o único parâmetro que vai ao servidor. Os dois na URL: abrir a ficha de
  // alguém e voltar devolve a mesma lista.
  const { estado: filtros, definir } = useEstadoNaUrl(PADRAO_FUNCIONARIOS);
  const showInativos = filtros.inativos;
  const busca = filtros.q;
  const { data: funcionarios = [], isLoading } = useFuncionarios(showInativos);
  const { setores = [] } = useSetores();
  const { data: vinculosAtivos = [] } = useVinculosAtivos();
  const { data: quinzenaAtiva } = useQuinzenaAtiva();
  const create = useCreateFuncionario();
  const update = useUpdateFuncionario();
  const remove = useDeleteFuncionario();
  const { toast } = useToast();
  const qc = useQueryClient();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Funcionario | null>(null);
  const [form, setForm] = useState(emptyForm);
  // Perfil escolhido na tela (3 opções). No banco só existem 2 regimes: escolher
  // "Encarregado" apenas REVELA a seção de setores — não grava um terceiro valor.
  const [perfil, setPerfil] = useState<PerfilFuncionario>("DIARISTA");

  // I15 — os vínculos de setor são ESTADO DO FORMULÁRIO, gravados no mesmo Salvar
  // do funcionário. Não são mais gravados na hora do "Vincular": era isso que
  // impedia virar Encarregado direto de Diarista (o servidor lia o regime
  // persistido, ainda DIARISTA) e fazia o Cancelar não desfazer nada.
  const [vinculosForm, setVinculosForm] = useState<VinculoForm[]>([]);
  // De quem é o estado carregado agora — impede que um refetch do react-query
  // sobrescreva o que o gestor acabou de digitar.
  const [vinculosSeedDe, setVinculosSeedDe] = useState<string | null>(null);
  const { data: vinculosDoEditando = [], isLoading: vinculosLoading } =
    useVinculosDoFuncionario(editing?.id);

  const persistidosAbertos = vinculosDoEditando.filter(v => !v.fim);
  const historicoVinculos = vinculosDoEditando.filter(v => v.fim);

  useEffect(() => {
    if (!open || !editing) return;
    if (vinculosSeedDe === editing.id) return;
    if (vinculosLoading) return;
    setVinculosForm(
      vinculosDoEditando
        .filter(v => !v.fim)
        .map(v => ({ id: v.id, setorId: v.setorId, inicio: v.inicio })),
    );
    setVinculosSeedDe(editing.id);
  }, [open, editing, vinculosLoading, vinculosDoEditando, vinculosSeedDe]);

  const isMensalista = perfil !== "DIARISTA";
  const regimeOriginal = editing ? (editing.tipoRemuneracao ?? "DIARISTA") : "DIARISTA";
  const regimeNovo = isMensalista ? "MENSALISTA" : "DIARISTA";
  const mudouRegimeComQuinzenaAberta =
    !!editing && regimeOriginal !== regimeNovo && quinzenaAtiva?.status === "ABERTA";
  // §5 — prévia de "quantas diárias desta quinzena serão recalculadas": mesma
  // fonte que o Financeiro já usa, filtrada pra quem está sendo editado. Só
  // busca quando o aviso pode aparecer — não precisa da lista inteira sempre.
  const { data: diariasQuinzenaAtiva = [] } = useDiariasByQuinzena(
    mudouRegimeComQuinzenaAberta ? quinzenaAtiva?.id : undefined,
  );
  const nDiariasAfetadas = editing
    ? diariasQuinzenaAtiva.filter(d => d.funcionarioId === editing.id).length
    : 0;
  // I15 no client — o servidor recusa igual, mas travar o Salvar aqui explica o
  // que fazer em vez de devolver um 400 depois do clique.
  const vinculoComDiarista = regimeNovo === "DIARISTA" && vinculosForm.length > 0;

  const handleReativar = async (id: string) => {
    await apiRequest("PATCH", `/api/equipe/funcionarios/${id}`, { ativo: true });
    qc.invalidateQueries({ queryKey: ["/api/equipe/funcionarios"] });
    toast({ title: "Funcionário reativado!" });
  };

  /** Cancelar/fechar descarta TUDO, inclusive os vínculos ainda não gravados. */
  const fecharDialog = () => {
    setOpen(false);
    setVinculosForm([]);
    setVinculosSeedDe(null);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setPerfil("DIARISTA");
    // Cadastro novo já pode nascer Encarregado com setores — a lista começa vazia
    // e não depende de nenhum `id`, porque nada é gravado antes do Salvar.
    setVinculosForm([]);
    setVinculosSeedDe("novo");
    setOpen(true);
  };
  const openEdit = (f: Funcionario) => {
    setEditing(f);
    setPerfil(perfilDoFuncionario(f, vinculosAtivos));
    setForm({
      nome: f.nome, apelido: f.apelido ?? "", cargo: f.cargo,
      valorDiaria: f.valorDiaria, pixChave: f.pixChave ?? "", pixTipo: f.pixTipo ?? "",
      salarioMensal: f.salarioMensal ?? "",
      formaPagamento: f.formaPagamento ?? "MENSAL",
      adiantamentoPct: f.adiantamentoPct ?? "50",
      variavelMaxMensal: f.variavelMaxMensal ?? "0",
      valorKgColheita: f.valorKgColheita ?? "",
    });
    // O seed dos vínculos vem no useEffect, quando a query do funcionário chegar.
    setVinculosForm([]);
    setVinculosSeedDe(null);
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload: Record<string, unknown> = {
        nome: form.nome.trim(),
        apelido: form.apelido.trim() || undefined,
        cargo: form.cargo,
        valorDiaria: form.valorDiaria,
        pixChave: form.pixChave.trim() || undefined,
        pixTipo: form.pixTipo.trim() || undefined,
        // Dois regimes no banco: "Encarregado" é mensalista com setor vinculado.
        tipoRemuneracao: regimeNovo,
        // I15 — os vínculos vão NO MESMO payload, gravados na mesma transação. É o
        // que permite mudar o regime e criar vínculo num Salvar só: o servidor
        // valida o estado FINAL, não o que estava no banco antes.
        vinculosSetor: vinculosForm.map(v => ({ id: v.id, setorId: v.setorId, inicio: v.inicio })),
      };
      if (isMensalista) {
        payload.salarioMensal = form.salarioMensal;
        payload.formaPagamento = form.formaPagamento;
        payload.adiantamentoPct = form.formaPagamento === "QUINZENAL" ? form.adiantamentoPct : undefined;
        payload.valorKgColheita = form.valorKgColheita.trim() || undefined;
        // Teto da variável (a Fase 2 usa; aqui só guarda). Tirar o último setor NÃO
        // zera o teto — o valor fica guardado para quando ele voltar a ter setor.
        payload.variavelMaxMensal = form.variavelMaxMensal;
      }
      let resultado: { diariasRecalculadas?: number } | undefined;
      if (editing) resultado = await update.mutateAsync({ id: editing.id, data: payload as any });
      else await create.mutateAsync(payload as any);
      // Os vínculos mudaram junto — invalidar para o rótulo "Encarregado" e o
      // "também: ..." das outras telas refletirem na hora. A diária pode ter
      // mudado também (§5) — invalidar pro Financeiro não ficar com dado velho.
      qc.invalidateQueries({ queryKey: ["/api/equipe/funcionario-setor"] });
      if (resultado?.diariasRecalculadas) {
        qc.invalidateQueries({ queryKey: ["/api/equipe/diarias"] });
        qc.invalidateQueries({ queryKey: ["/api/equipe/quinzenas"] });
      }
      toast({
        title: editing ? "Funcionário atualizado!" : "Funcionário cadastrado!",
        description: resultado?.diariasRecalculadas
          ? `${resultado.diariasRecalculadas} diária${resultado.diariasRecalculadas !== 1 ? "s" : ""} da quinzena aberta recalculada${resultado.diariasRecalculadas !== 1 ? "s" : ""}.`
          : undefined,
      });
      fecharDialog();
    } catch (e: any) {
      const msg = String(e?.message ?? "");
      // A mensagem do servidor é específica (I10/I15, setor repetido, nome duplicado)
      // — mostrá-la é melhor que um "Erro ao salvar" que não diz o que fazer.
      const doServidor = (() => {
        try {
          return JSON.parse(msg.slice(msg.indexOf(":") + 1).trim())?.error as string | undefined;
        } catch {
          // Corpo não-JSON: cai nos rótulos genéricos abaixo. Nunca engolir calado.
          return undefined;
        }
      })();
      toast({
        title: doServidor
          ?? (msg.includes("409") ? "Nome já cadastrado"
            : /sal[áa]rio/i.test(msg) ? "Mensalista exige salário mensal"
            : "Erro ao salvar"),
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    await remove.mutateAsync(id);
    toast({ title: "Funcionário desativado." });
  };

  const field = (key: keyof typeof form) => ({
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [key]: e.target.value })),
  });

  // Perfil é CATEGORIA, não estado: não muda por ação do usuário e não há nada
  // a "corrigir" nele. Por isso outline neutro, diferenciado só pelo texto —
  // pintar na escala semântica codificaria "Diarista = sucesso", que é bobagem.
  // Os tons de `PERFIL_META.badge`/`.dot` continuam no seletor do formulário,
  // onde servem para distinguir as três opções na hora de escolher.
  const perfilBadge = (f: Funcionario) => (
    <Badge variant="outline" className="text-xs font-normal">
      {PERFIL_META[perfilDoFuncionario(f, vinculosAtivos)].label}
    </Badge>
  );

  /** Setores sob um encarregado — vínculos abertos, nome resolvido. */
  const setoresDoEncarregado = (funcionarioId: string) =>
    vinculosAtivos
      .filter(v => v.funcionarioId === funcionarioId && !v.fim)
      .map(v => setores.find(s => s.id === v.setorId)?.nome)
      .filter((n): n is string => !!n)
      .sort((a, b) => a.localeCompare(b));

  /** Valor do dia/mês, ou `null` quando não há valor cadastrado. */
  const valorDe = (f: Funcionario) => {
    const mensalista = (f.tipoRemuneracao ?? "DIARISTA") === "MENSALISTA";
    const bruto = parseFloat((mensalista ? f.salarioMensal : f.valorDiaria) ?? "0");
    if (!(bruto > 0)) return null;
    return `R$ ${bruto.toFixed(2)} ${mensalista ? "/mês" : "/dia"}`;
  };

  const visiveis = funcionarios.filter(f => combina(busca, f.nome, f.apelido));
  // Quantos aparecem com "sem valor" — o rodapé conta, porque um sozinho passa
  // batido numa lista de 30 e o efeito só aparece no fechamento da quinzena.
  const semValor = visiveis.filter(f => valorDe(f) === null).length;

  const salarioInvalido = isMensalista && !(parseFloat(form.salarioMensal) > 0);

  return (
    <div className="md:p-6 space-y-4 md:space-y-6">
      <PageHeader
        titulo="Funcionários"
        acao={
          <div className="flex items-center gap-3 md:gap-4">
            <label className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <Switch checked={showInativos} onCheckedChange={v => definir({ inativos: v })} />
              Mostrar inativos
            </label>
            <Button size="sm" className={ALVO_TOQUE_BOTAO} onClick={openCreate}><Plus className="h-4 w-4 mr-2" />Novo</Button>
          </div>
        }
      />

      <label className="flex sm:hidden items-center gap-2 text-sm text-muted-foreground cursor-pointer">
        <Switch checked={showInativos} onCheckedChange={v => definir({ inativos: v })} />
        Mostrar inativos
      </label>

      <PageToolbar
        fim={<PageSearch valor={busca} onChange={v => definir({ q: v })} placeholder="Nome" testId="input-busca-funcionarios" />}
      >
        <span />
      </PageToolbar>

      {isLoading ? (
        <PageSkeleton variante="tabela" linhas={5} />
      ) : funcionarios.length === 0 ? (
        <EmptyState
          icone={Users}
          titulo="Nenhum funcionário cadastrado"
          descricao="Sem funcionário não há tarefa, diária nem folha — comece por aqui."
          acao={<Button onClick={openCreate}><Plus className="h-4 w-4 mr-2" />Novo funcionário</Button>}
          testId="empty-funcionarios"
        />
      ) : visiveis.length === 0 ? (
        <NadaEncontrado termo={busca} />
      ) : (
        <>
          {/* ── Mobile: cards (o uso real é no celular, no campo) ──
              `bg-card` e não `bg-card`: no tema escuro o card branco deixava o
              texto claro ilegível. A opacidade de inativo fica só no conteúdo —
              nunca nos botões, que é onde mora o Reativar. */}
          <AdminLista>
            {visiveis.map(f => {
              const valor = valorDe(f);
              const setoresSob = setoresDoEncarregado(f.id);
              return (
                <AdminListaItem
                  key={f.id}
                  testId={`funcionario-item-${f.id}`}
                  atenuado={!f.ativo}
                  identidade={
                    <Link href={`/equipe/ficha/${f.id}`} state={origemAtual()} className="hover:underline">
                      {f.apelido ? `${f.apelido} (${f.nome})` : f.nome}
                    </Link>
                  }
                  meta={
                    <>
                      {perfilBadge(f)}
                      {!f.ativo && <Badge variant="outline" className="text-xs text-muted-foreground">Inativo</Badge>}
                      {/* "R$ 0,00 /dia" lia como valor real — e um zero silencioso
                          só aparece no fechamento da quinzena. Agora é estado. */}
                      {valor
                        ? <span>{valor}</span>
                        : <StatusBadge tom="atencao">sem valor</StatusBadge>}
                      {setoresSob.length > 0 && <span>Setores: {setoresSob.join(", ")}</span>}
                    </>
                  }
                  acoes={
                    f.ativo ? (
                      <>
                        <Button size="icon" variant="ghost" className={ALVO_TOQUE} aria-label={`Editar ${f.nome}`} onClick={() => openEdit(f)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className={ALVO_TOQUE} aria-label={`Desativar ${f.nome}`} onClick={() => handleDelete(f.id)}>
                          <UserX className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </>
                    ) : (
                      <Button size="icon" variant="ghost" className={ALVO_TOQUE} title="Reativar" aria-label={`Reativar ${f.nome}`} onClick={() => handleReativar(f.id)}>
                        <UserCheck className="h-4 w-4 text-sucesso" />
                      </Button>
                    )
                  }
                />
              );
            })}
          </AdminLista>

          {/* ── Desktop: tabela ── */}
          <AdminTabela>
            <DataTable colunas={COLUNAS_FUNCIONARIOS}>
              <DataTableHeader>
                <DataTableRow>
                  <DataTableHead>Nome</DataTableHead>
                  <DataTableHead>Apelido</DataTableHead>
                  <DataTableHead>Perfil</DataTableHead>
                  <DataTableHead>Setores</DataTableHead>
                  <DataTableHead num>Diária / salário</DataTableHead>
                  <DataTableHead>PIX</DataTableHead>
                  <DataTableHead />
                </DataTableRow>
              </DataTableHeader>
              <DataTableBody>
                {visiveis.map(f => {
                  const valor = valorDe(f);
                  const setoresSob = setoresDoEncarregado(f.id);
                  return (
                    <DataTableRow key={f.id}>
                      {/* O nome é a coluna que responde à pergunta da tela. */}
                      <DataTableCell destaque className={!f.ativo ? "opacity-50" : ""}>
                        <Link href={`/equipe/ficha/${f.id}`} state={origemAtual()} className="truncate block hover:underline">
                          {f.nome}
                        </Link>
                      </DataTableCell>
                      <DataTableCell className={`text-muted-foreground truncate ${!f.ativo ? "opacity-50" : ""}`}>
                        {f.apelido ?? "—"}
                      </DataTableCell>
                      <DataTableCell>
                        <div className="flex items-center gap-1.5">
                          {perfilBadge(f)}
                          {!f.ativo && <Badge variant="outline" className="text-xs text-muted-foreground">Inativo</Badge>}
                        </div>
                      </DataTableCell>
                      {/* Encarregado sem os setores embaixo dele é meia informação:
                          é o vínculo que o torna encarregado. */}
                      <DataTableCell className="text-xs text-muted-foreground truncate" title={setoresSob.join(", ")}>
                        {setoresSob.length > 0 ? setoresSob.join(", ") : "—"}
                      </DataTableCell>
                      <DataTableNum className={!f.ativo ? "opacity-50" : ""}>
                        {valor ?? <StatusBadge tom="atencao">sem valor</StatusBadge>}
                      </DataTableNum>
                      <DataTableCell className={`text-xs text-muted-foreground truncate ${!f.ativo ? "opacity-50" : ""}`}>
                        {f.pixChave ?? "—"}
                      </DataTableCell>
                      <DataTableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          {f.ativo ? (
                            <>
                              <Button size="icon" variant="ghost" onClick={() => openEdit(f)}><Pencil className="h-4 w-4" /></Button>
                              <Button size="icon" variant="ghost" onClick={() => handleDelete(f.id)}><UserX className="h-4 w-4 text-muted-foreground" /></Button>
                            </>
                          ) : (
                            <Button size="icon" variant="ghost" title="Reativar" onClick={() => handleReativar(f.id)}>
                              <UserCheck className="h-4 w-4 text-sucesso" />
                            </Button>
                          )}
                        </div>
                      </DataTableCell>
                    </DataTableRow>
                  );
                })}
              </DataTableBody>
              <DataTableFooter>
                <DataTableRow className="hover:bg-transparent">
                  <DataTableCell colSpan={7} className="text-xs text-muted-foreground">
                    {visiveis.length} funcionário(s)
                    {semValor > 0 && (
                      <>
                        {" · "}
                        <StatusBadge tom="atencao">{semValor} sem valor cadastrado</StatusBadge>
                      </>
                    )}
                  </DataTableCell>
                </DataTableRow>
              </DataTableFooter>
            </DataTable>
          </AdminTabela>
        </>
      )}

      {/* Fechar por fora (Esc, clique no backdrop) descarta os vínculos igual ao
          Cancelar — senão a lista sobreviveria para o próximo funcionário aberto. */}
      <Dialog open={open} onOpenChange={v => (v ? setOpen(true) : fecharDialog())}>
        <DialogContent className="max-h-[90vh] overflow-y-auto max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Editar Funcionário" : "Novo Funcionário"}</DialogTitle></DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Nome *</Label>
              <Input {...field("nome")} placeholder="Nome completo" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Apelido</Label>
                <Input {...field("apelido")} placeholder="Ex: Dáu, Mineiro" />
              </div>
              <div className="space-y-1">
                <Label>Cargo</Label>
                <Input {...field("cargo")} placeholder="Diarista" />
              </div>
            </div>

            {/* ── Perfil: 3 opções na tela, 2 regimes no banco ── */}
            <div className="space-y-1.5">
              <Label>Perfil</Label>
              <div className="grid grid-cols-3 gap-2">
                {PERFIS.map(p => {
                  const meta = PERFIL_META[p];
                  const ativo = perfil === p;
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPerfil(p)}
                      className={`rounded-xl border px-2 py-2.5 text-xs font-medium transition-colors ${
                        ativo ? meta.badge : "bg-card text-muted-foreground border-border"
                      }`}
                    >
                      <span className="block text-base leading-none mb-1">{meta.dot}</span>
                      {meta.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">{PERFIL_META[perfil].descricao}</p>
            </div>

            {mudouRegimeComQuinzenaAberta && (
              <div className="flex gap-2 rounded-lg border border-atencao/30 bg-atencao-suave p-3 text-xs text-atencao">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <p>
                  {nDiariasAfetadas > 0 ? (
                    <>
                      <strong>{nDiariasAfetadas} diária{nDiariasAfetadas !== 1 ? "s" : ""} desta quinzena será{nDiariasAfetadas !== 1 ? "ão" : ""} recalculada{nDiariasAfetadas !== 1 ? "s" : ""}</strong>{" "}
                      pra bater com o regime novo.{" "}
                    </>
                  ) : null}
                  {regimeNovo === "MENSALISTA"
                    ? "A partir de agora as tarefas dele viram registro sem valor."
                    : "A partir de agora as tarefas dele voltam a gerar diária com valor."}{" "}
                  Quinzenas já fechadas não mudam.
                </p>
              </div>
            )}

            {!isMensalista ? (
              <div className="space-y-1">
                <Label>Valor da Diária (R$)</Label>
                <Input {...field("valorDiaria")} type="number" inputMode="decimal" step="0.01" />
              </div>
            ) : (
              <>
                <div className="space-y-1">
                  <Label>Salário mensal (R$) *</Label>
                  <Input {...field("salarioMensal")} type="number" inputMode="decimal" step="0.01" placeholder="2200.00" />
                  {salarioInvalido && (
                    <p className="text-xs text-erro">Mensalista exige salário mensal maior que zero.</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label>Forma de pagamento</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {([
                      { v: "MENSAL", label: "Uma vez por mês" },
                      { v: "QUINZENAL", label: "Adiantamento quinzenal" },
                    ] as const).map(o => (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => setForm(f => ({ ...f, formaPagamento: o.v }))}
                        className={`rounded-xl border px-3 py-2.5 text-xs font-medium transition-colors ${
                          form.formaPagamento === o.v
                            ? "bg-sucesso-suave text-sucesso border-sucesso/30"
                            : "bg-card text-muted-foreground border-border"
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* O campo de % só aparece se escolher adiantamento quinzenal */}
                {form.formaPagamento === "QUINZENAL" && (
                  <div className="space-y-1">
                    <Label>% do salário na quinzena</Label>
                    <Input {...field("adiantamentoPct")} type="number" inputMode="decimal" step="0.01" />
                    <p className="text-xs text-muted-foreground">
                      Guardado agora, usado no adiantamento da fase seguinte. Nada sai na quinzena nesta versão.
                    </p>
                  </div>
                )}

                {perfil === "ENCARREGADO" && (
                  <div className="space-y-1">
                    <Label>Teto da variável mensal (R$)</Label>
                    <Input {...field("variavelMaxMensal")} type="number" inputMode="decimal" step="0.01" />
                    <p className="text-xs text-muted-foreground">
                      Só o teto. A variável é calculada na auditoria (fase seguinte).
                    </p>
                  </div>
                )}

                <div className="space-y-1">
                  <Label>R$ por kg colhido (opcional)</Label>
                  <Input {...field("valorKgColheita")} type="number" inputMode="decimal" step="0.0001" placeholder="0.0700" />
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Tipo PIX</Label>
                <Input {...field("pixTipo")} placeholder="CPF, TELEFONE..." />
              </div>
              <div className="space-y-1">
                <Label>Chave PIX</Label>
                <Input {...field("pixChave")} placeholder="Chave PIX" />
              </div>
            </div>

            {/* ── Setores do encarregado ──
                Aparece no perfil Encarregado E sempre que ainda houver setor na
                lista: sem isso, quem trocasse para Diarista perderia o único jeito
                de tirar os setores e ficaria travado no bloqueio do I15. */}
            {(perfil === "ENCARREGADO" || vinculosForm.length > 0) && (
              <SetoresDoEncarregado
                funcionarioId={editing?.id}
                vinculos={vinculosForm}
                onChange={setVinculosForm}
                persistidosAbertos={persistidosAbertos}
                historico={historicoVinculos}
                carregando={!!editing && vinculosLoading}
                podeVincular={perfil === "ENCARREGADO"}
              />
            )}
            {vinculoComDiarista && (
              <p className="rounded-lg border border-erro/30 bg-erro-suave p-3 text-xs text-erro">
                Diarista não pode ser encarregado de setor. Tire os {vinculosForm.length} setor(es) da
                lista acima (o ×) — eles serão encerrados ao salvar, com o histórico preservado.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={fecharDialog}>Cancelar</Button>
            <Button
              onClick={handleSave}
              disabled={
                !form.nome.trim() || salarioInvalido || vinculoComDiarista
                || create.isPending || update.isPending
              }
            >
              {create.isPending || update.isPending ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
